import { useState, useCallback, useMemo } from 'react'
import {
  TWeekDay,
  IWeeklySchedule,
  WEEK_DAYS,
  ISessionDurationRange,
  TOrganizationType,
} from 'pages/Planning/types'

export interface IValidationError {
  type: 'error' | 'warning'
  message: string
  details?: string
}

interface UseWeeklyStudyReturn {
  weeklyHours: number
  weeklySchedule: IWeeklySchedule
  sessionDurationRange: ISessionDurationRange
  organizationType: TOrganizationType
  updateWeeklyHours: (hours: number) => void
  updateDaySelection: (dayKey: TWeekDay, isSelected: boolean) => void
  updateMinDuration: (minutes: number) => void
  updateMaxDuration: (minutes: number) => void
  updateOrganizationType: (type: TOrganizationType) => void
  getTotalWeeklyHours: () => number
  getSelectedDays: () => TWeekDay[]
  isStepCompleted: () => boolean
  validationErrors: IValidationError[]
  hasValidationErrors: boolean
  selectedDisciplinesCount?: number
  setSelectedDisciplinesCount: (count: number) => void
}

export const useWeeklyStudy = (): UseWeeklyStudyReturn => {
  const [weeklyHours, setWeeklyHours] = useState<number>(0)
  const [selectedDisciplinesCount, setSelectedDisciplinesCount] = useState<number>(0)
  const [organizationType, setOrganizationType] = useState<TOrganizationType>('cycle')

  const [sessionDurationRange, setSessionDurationRange] = useState<ISessionDurationRange>({
    minDuration: 30, // 30 minutes default
    maxDuration: 120, // 2 hours default (at least 60 minutes as per requirements)
  })

  const [weeklySchedule, setWeeklySchedule] = useState<IWeeklySchedule>(() => {
    // Initialize with default values for all days
    const initialSchedule: IWeeklySchedule = {}
    WEEK_DAYS.forEach((day) => {
      initialSchedule[day.key] = {
        isSelected: false,
      }
    })
    return initialSchedule
  })

  const updateWeeklyHours = useCallback((hours: number) => {
    setWeeklyHours(hours)
  }, [])

  const updateDaySelection = useCallback((dayKey: TWeekDay, isSelected: boolean) => {
    setWeeklySchedule((prev) => ({
      ...prev,
      [dayKey]: {
        ...prev[dayKey],
        isSelected,
      },
    }))
  }, [])

  const updateMinDuration = useCallback((minutes: number) => {
    setSessionDurationRange((prev) => {
      const newMin = minutes
      // Ensure max is not less than min
      const newMax = prev.maxDuration < newMin ? newMin : prev.maxDuration
      return {
        minDuration: newMin,
        maxDuration: newMax,
      }
    })
  }, [])

  const updateMaxDuration = useCallback((minutes: number) => {
    setSessionDurationRange((prev) => {
      // Ensure max is not less than min
      if (minutes >= prev.minDuration) {
        return {
          ...prev,
          maxDuration: minutes,
        }
      }
      return prev
    })
  }, [])

  const updateOrganizationType = useCallback((type: TOrganizationType) => {
    setOrganizationType(type)
  }, [])

  const getTotalWeeklyHours = useCallback(() => {
    // For cycle type, return weekly hours directly (no days needed)
    if (organizationType === 'cycle') {
      return weeklyHours
    }

    // For schedule type, check if days are selected
    const selectedDaysCount = Object.values(weeklySchedule).filter((day) => day.isSelected).length
    if (selectedDaysCount === 0) return 0
    // Distribute weekly hours evenly across selected days
    return weeklyHours
  }, [weeklySchedule, weeklyHours, organizationType])

  const getSelectedDays = useCallback(() => {
    return Object.entries(weeklySchedule)
      .filter(([_, day]) => day.isSelected)
      .map(([dayKey, _]) => dayKey as TWeekDay)
  }, [weeklySchedule])

  /**
   * Comprehensive validation logic for weekly study configuration
   *
   * This validation ensures that:
   * 1. Minimum duration is not greater than maximum
   * 2. Weekly hours is sufficient for at least one session
   * 3. Weekly hours can accommodate all selected disciplines
   * 4. At least one day is selected
   * 5. Distribution across days is mathematically possible
   * 6. Session duration range allows for flexible distribution
   * 7. Daily hours distribution is realistic (considering a day has 24 hours)
   *
   * Returns an array of errors/warnings with detailed messages
   */
  const validationErrors = useMemo((): IValidationError[] => {
    const errors: IValidationError[] = []
    const selectedDays = Object.values(weeklySchedule).filter((day) => day.isSelected).length
    const weeklyHoursInMinutes = weeklyHours * 60
    const { minDuration, maxDuration } = sessionDurationRange

    // Basic field validation - don't show errors if user hasn't started configuring yet
    // For cycle type, we don't need selected days
    if (organizationType === 'cycle') {
      if (weeklyHours === 0) {
        return errors
      }
    } else {
      // For schedule type, we need both weekly hours and selected days
      if (weeklyHours === 0 || selectedDays === 0) {
        return errors
      }
    }

    // 1. Validate minimum duration is not greater than maximum
    if (minDuration > maxDuration) {
      errors.push({
        type: 'error',
        message: 'Duração mínima não pode ser maior que a duração máxima',
        details: `Mínimo: ${minDuration}min, Máximo: ${maxDuration}min`,
      })
    }

    // 2. Validate weekly hours is sufficient for minimum session duration
    if (weeklyHoursInMinutes < minDuration) {
      errors.push({
        type: 'error',
        message: 'Carga horária semanal insuficiente',
        details: `A carga horária total (${weeklyHours}h) é menor que a duração mínima de uma sessão (${minDuration}min). Aumente a carga horária ou reduza a duração mínima.`,
      })
    }

    // 3. Validate if there are enough selected disciplines
    if (selectedDisciplinesCount > 0) {
      // Calculate minimum hours needed to cover all disciplines with minimum session duration
      const minHoursNeeded = (selectedDisciplinesCount * minDuration) / 60

      if (weeklyHours < minHoursNeeded) {
        errors.push({
          type: 'error',
          message: 'Carga horária insuficiente para as disciplinas selecionadas',
          details: `Com ${selectedDisciplinesCount} disciplina(s) e ${minDuration}min de sessão mínima, você precisa de pelo menos ${Math.ceil(
            minHoursNeeded
          )}h semanais. Atualmente: ${weeklyHours}h.`,
        })
      }
    }

    // 4. Validate number of days selected (only for schedule type)
    if (organizationType === 'schedule' && selectedDays === 0) {
      errors.push({
        type: 'error',
        message: 'Selecione pelo menos um dia da semana',
        details: 'É necessário selecionar ao menos um dia para distribuir as sessões de estudo.',
      })
    }

    // 5. Validate if distribution is mathematically possible
    // For cycle type, we don't validate based on selected days
    if (organizationType === 'schedule' && selectedDays > 0 && selectedDisciplinesCount > 0) {
      const avgHoursPerDay = weeklyHours / selectedDays
      const avgMinutesPerDay = avgHoursPerDay * 60

      // Check if average study time per day is too small
      if (avgMinutesPerDay < minDuration && weeklyHoursInMinutes >= minDuration) {
        errors.push({
          type: 'warning',
          message: 'Distribuição de tempo pode ser desafiadora',
          details: `Com ${selectedDays} dia(s) selecionado(s), a média é ${avgMinutesPerDay.toFixed(
            0
          )}min/dia. Considere aumentar a carga horária ou reduzir o número de dias.`,
        })
      }

      // Check if sessions can fit within the constraints
      const maxSessionsPerDay = Math.floor((avgHoursPerDay * 60) / minDuration)
      const totalPossibleSessions = maxSessionsPerDay * selectedDays

      if (totalPossibleSessions < selectedDisciplinesCount) {
        errors.push({
          type: 'error',
          message: 'Impossível distribuir todas as disciplinas',
          details: `Com a configuração atual, é possível criar no máximo ${totalPossibleSessions} sessão(ões), mas você tem ${selectedDisciplinesCount} disciplina(s). Aumente a carga horária, reduza a duração mínima ou selecione mais dias.`,
        })
      }
    }

    // 6. Check if minimum and maximum duration difference is too small
    if (maxDuration - minDuration < 15 && minDuration !== maxDuration) {
      errors.push({
        type: 'warning',
        message: 'Intervalo de duração muito estreito',
        details:
          'Um intervalo maior entre duração mínima e máxima permite uma distribuição mais flexível das sessões.',
      })
    }

    // 7. Validate if daily hours distribution is realistic (only for schedule type)
    if (organizationType === 'schedule' && selectedDays > 0) {
      const avgHoursPerDay = weeklyHours / selectedDays

      // Warn if daily average exceeds 24 hours (physically impossible)
      if (avgHoursPerDay > 24) {
        errors.push({
          type: 'warning',
          message: 'Distribuição de carga horária irrealista',
          details: `Com ${selectedDays} dia${selectedDays > 1 ? 's' : ''} selecionado${
            selectedDays > 1 ? 's' : ''
          } e ${weeklyHours}h semanais, a média seria ${avgHoursPerDay.toFixed(
            1
          )}h por dia. Seu planejamento será inviável.`,
        })
      }
      // Warn if daily average is very high (unrealistic for study)
      else if (avgHoursPerDay > 16) {
        errors.push({
          type: 'warning',
          message: 'Carga horária diária muito alta',
          details: `Com ${selectedDays} dia(s) selecionado(s), você precisaria estudar ${avgHoursPerDay.toFixed(
            1
          )}h por dia. Considere distribuir a carga em mais dias para tornar o planejamento mais realista e sustentável.`,
        })
      }
    }

    return errors
  }, [
    weeklyHours,
    weeklySchedule,
    sessionDurationRange,
    selectedDisciplinesCount,
    organizationType,
  ])

  const hasValidationErrors = useMemo(() => {
    return validationErrors.some((error) => error.type === 'error')
  }, [validationErrors])

  const isStepCompleted = useCallback(() => {
    let hasBasicRequirements = false

    if (organizationType === 'cycle') {
      // For cycle type, only need weekly hours (no days selection required)
      hasBasicRequirements = weeklyHours > 0
    } else {
      // For schedule type, need both weekly hours and selected days
      const selectedDays = getSelectedDays()
      const totalHours = getTotalWeeklyHours()
      hasBasicRequirements = selectedDays.length > 0 && totalHours > 0 && weeklyHours > 0
    }

    // Step is completed only if basic requirements are met AND there are no validation errors
    return hasBasicRequirements && !hasValidationErrors
  }, [weeklyHours, getSelectedDays, getTotalWeeklyHours, hasValidationErrors, organizationType])

  return {
    weeklyHours,
    weeklySchedule,
    sessionDurationRange,
    organizationType,
    updateWeeklyHours,
    updateDaySelection,
    updateMinDuration,
    updateMaxDuration,
    updateOrganizationType,
    getTotalWeeklyHours,
    getSelectedDays,
    isStepCompleted,
    validationErrors,
    hasValidationErrors,
    selectedDisciplinesCount,
    setSelectedDisciplinesCount,
  }
}
